import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop, LogOut, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';

/** Una sesión viva, tal y como la devuelve `GET /auth/sessions`. */
interface Sesion {
  id: number;
  deviceLabel: string | null;
  ip: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  /** La del navegador desde el que se está mirando. */
  current: boolean;
}

/** Lo que devuelve `GET /auth/sessions`. */
interface Respuesta {
  devices: Sesion[];
  limit: number;
  /**
   * Dispositivos distintos usados hoy.
   *
   * No es `devices.length`: el tope deja como mucho tres vivas, así que quince
   * entradas desde quince móviles se verían como un uso normal si sólo se
   * mirara lo vivo.
   */
  recentDeviceCount: number;
}

/**
 * Mis dispositivos.
 *
 * Existe porque el tope de dispositivos por asiento cierra la sesión más
 * antigua sin preguntar, y una expulsión que no se puede ver ni explicar es
 * indistinguible de un fallo. Aquí se ve quién está dentro, desde dónde y
 * cuándo fue la última vez — y se puede cerrar lo que sobre.
 *
 * **Cerrar el dispositivo actual no se ofrece.** Para eso está el botón de
 * salir del menú de perfil; ponerlo también aquí invita a cerrarse la sesión
 * a uno mismo por error mientras se intenta echar a otro.
 */
export function SessionsPage() {
  const t = useT();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: queryKeys.auth.sessions,
    queryFn: () => api.get<Respuesta>('/auth/sessions'),
  });

  const cerrar = useMutation({
    mutationFn: (id: number) => api.delete(`/auth/sessions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.auth.sessions });
      toast({ tone: 'success', title: t('sessions.closed') });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('sessions.closeFailed'), description: e.message }),
  });

  const sesiones = q.data?.devices ?? [];
  const TOPE = q.data?.limit ?? 3;
  const usadosHoy = q.data?.recentDeviceCount ?? 0;

  return (
    <div className="page space-y-6">
      <Breadcrumbs
        items={[{ label: t('settings.title'), to: '/settings' }, { label: t('sessions.title') }]}
      />

      <header>
        <h1 className="text-3xl font-bold">{t('sessions.title')}</h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--color-fg-muted)]">
          {t('sessions.subtitle', { n: TOPE })}
        </p>
      </header>

      {q.isLoading ? (
        <Skeleton className="h-40" />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[var(--color-accent-ink)]" />
              {t('sessions.count', { n: sesiones.length, max: TOPE })}
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-[var(--color-border-subtle)]">
            {sesiones.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 py-3">
                <Laptop className="h-4 w-4 flex-none text-[var(--color-fg-muted)]" />

                <div className="min-w-[10rem] flex-1">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {s.deviceLabel ?? t('sessions.unknownDevice')}
                    {s.current && (
                      <span className="rounded-full border border-[var(--color-accent-ink)] px-2 py-px text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-accent-ink)]">
                        {t('sessions.thisDevice')}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--color-fg-muted)]">
                    {s.ip ? `${s.ip} · ` : ''}
                    {t('sessions.lastSeen', { when: cuando(s.lastSeenAt ?? s.createdAt, t) })}
                  </div>
                </div>

                {/* El actual no se puede cerrar desde aquí. Ver la nota de clase. */}
                {!s.current && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={cerrar.isPending}
                    onClick={() => cerrar.mutate(s.id)}
                    leftIcon={<LogOut className="h-4 w-4" />}
                  >
                    {t('sessions.close')}
                  </Button>
                )}
              </div>
            ))}

            {/*
              La señal de cuenta compartida.

              Se enseña a partir del doble del tope, no al pasarse por uno: usar
              cuatro dispositivos en un día es una tarde de trabajo normal, y
              avisar ahí sería acusar a quien no ha hecho nada raro. A partir de
              seis ya no es un descuido, y entonces la frase útil no es un
              reproche sino que hay una forma legítima de hacerlo.
            */}
            {usadosHoy >= TOPE * 2 && (
              <p className="pt-3 text-[11px] leading-snug text-[var(--color-warning,#8a5c0f)]">
                {t('sessions.sharedHint', { n: usadosHoy })}
              </p>
            )}

            {/* El tope importa cuando se está cerca, no siempre. */}
            {sesiones.length >= TOPE && (
              <p
                className={cn(
                  'pt-3 text-[11px] leading-snug',
                  'text-[var(--color-fg-muted)]',
                )}
              >
                {t('sessions.atLimit', { n: TOPE })}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * «hace 5 minutos», «hace 2 horas», «el 9 de agosto».
 *
 * Relativo mientras sea reciente, porque para decidir si una sesión es tuya lo
 * que importa es si está activa AHORA; una fecha absoluta obliga a calcular.
 * Pasado un día ya no hay nada que decidir y la fecha es más clara.
 */
function cuando(
  iso: string,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return t('sessions.now');
  if (min < 60) return t('sessions.minutesAgo', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('sessions.hoursAgo', { n: h });
  return new Date(iso).toLocaleDateString();
}
